import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Filter, Heart, MessageCircle, Clock, User, Calendar, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ROUTE_PATHS, CATEGORY_LABELS, type ForumQuestion } from '@/lib/index';
import { supabase } from '@/integrations/supabase/client';

const mockQuestions: ForumQuestion[] = [
  {
    id: '1',
    user_id: 'user1',
    email: 'user1@example.com',
    question: 'Kada je najbolje vreme za promenu karijere prema mojoj natalnoj karti?',
    category: 'karijera',
    is_anonymous: false,
    display_name: 'Marija P.',
    status: 'answered',
    answer: 'Prema vašoj natalnoj karti, Saturn tranzit kroz vašu 10. kuću karijere će biti najjači u aprilu 2026. To je idealno vreme za ozbiljne promene. Jupiter u vašoj 2. kući finansija takođe podržava nove prilike. Preporučujem da počnete pripreme sada, a konkretne korake napravite u martu-aprilu.',
    answered_at: '2026-03-20T10:30:00Z',
    created_at: '2026-03-15T14:20:00Z',
    likes: 24
  },
  {
    id: '2',
    user_id: 'user2',
    email: 'user2@example.com',
    question: 'Da li će moja veza preživeti ovaj težak period? Osećam veliku distancu.',
    category: 'ljubav',
    is_anonymous: true,
    display_name: 'Anonimno',
    status: 'answered',
    answer: 'Trenutni tranzit Saturna kroz vašu 7. kuću partnerstva donosi testove i izazove u vezi. Ovo nije kraj, već poziv na zrelost i dubinu. Venera u vašoj karti pokazuje da imate snagu da prevaziđete ovo. Ključ je iskrena komunikacija i strpljenje. Period će se olakšati nakon maja 2026.',
    answered_at: '2026-03-22T16:45:00Z',
    created_at: '2026-03-18T09:15:00Z',
    likes: 18
  },
  {
    id: '3',
    user_id: 'user3',
    email: 'user3@example.com',
    question: 'Imam zdravstvene probleme koji se ponavljaju. Šta moja karta kaže o tome?',
    category: 'zdravlje',
    is_anonymous: false,
    display_name: 'Stefan M.',
    status: 'answered',
    answer: 'Vaša 6. kuća zdravlja ima Pluton u tranzitu, što može ukazivati na potrebu za dubokom transformacijom životnih navika. Mars u vašoj karti sugeriše da stres može biti okidač. Preporučujem holistički pristup - yoga, meditacija, i pažnja na ishranu. Konsultujte se sa lekarom, ali i radite na emocionalnom zdravlju.',
    answered_at: '2026-03-25T11:20:00Z',
    created_at: '2026-03-20T13:40:00Z',
    likes: 15
  },
  {
    id: '4',
    user_id: 'user4',
    email: 'user4@example.com',
    question: 'Kada će se moja finansijska situacija poboljšati? Trenutno je veoma teška.',
    category: 'finansije',
    is_anonymous: true,
    display_name: 'Anonimno',
    status: 'pending',
    answer: null,
    answered_at: null,
    created_at: '2026-03-26T08:30:00Z',
    likes: 8
  },
  {
    id: '5',
    user_id: 'user5',
    email: 'user5@example.com',
    question: 'Da li je ova osoba prava za meni? Osećam snažnu povezanost ali i strah.',
    category: 'ljubav',
    is_anonymous: false,
    display_name: 'Ana K.',
    status: 'pending',
    answer: null,
    answered_at: null,
    created_at: '2026-03-27T15:10:00Z',
    likes: 12
  },
  {
    id: '6',
    user_id: 'user6',
    email: 'user6@example.com',
    question: 'Razmišljam o pokretanju biznisa. Da li je sada pravo vreme?',
    category: 'karijera',
    is_anonymous: false,
    display_name: 'Nikola D.',
    status: 'answered',
    answer: 'Jupiter u vašoj 2. kući finansija i Uran u 10. kući karijere kreiraju odličnu kombinaciju za preduzetništvo! Maj-jun 2026 su idealni meseci za pokretanje. Vaša natalna karta pokazuje prirodni talenat za inovacije. Samo budite strpljivi sa rezultatima - prave zarade dolaze nakon 6-8 meseci.',
    answered_at: '2026-03-23T14:00:00Z',
    created_at: '2026-03-19T11:25:00Z',
    likes: 21
  }
];

export default function ForumPage() {
  const [questions, setQuestions] = useState<ForumQuestion[]>([]);
  const [filteredQuestions, setFilteredQuestions] = useState<ForumQuestion[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('sve');
  const [statusFilter, setStatusFilter] = useState<string>('sve');
  const [loading, setLoading] = useState(true);

  // Load questions from database
  useEffect(() => {
    loadQuestions();
  }, []);

  const loadQuestions = async () => {
    try {
      const { data, error } = await supabase
        .from('forum_questions_2026_03_28')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setQuestions(data || []);
    } catch (error) {
      console.error('Error loading questions:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let filtered = questions;

    if (searchQuery) {
      filtered = filtered.filter(q => 
        q.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (q.answer && q.answer.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    if (categoryFilter !== 'sve') {
      filtered = filtered.filter(q => q.category === categoryFilter);
    }

    if (statusFilter !== 'sve') {
      filtered = filtered.filter(q => q.status === statusFilter);
    }

    setFilteredQuestions(filtered);
  }, [searchQuery, categoryFilter, statusFilter, questions]);

  const handleLike = (questionId: string) => {
    setQuestions(prev => prev.map(q => 
      q.id === questionId ? { ...q, likes: q.likes + 1 } : q
    ));
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `Pre ${diffMins} min`;
    if (diffHours < 24) return `Pre ${diffHours}h`;
    if (diffDays < 7) return `Pre ${diffDays} dana`;
    return date.toLocaleDateString('sr-RS', { day: 'numeric', month: 'short' });
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="relative overflow-hidden bg-gradient-to-b from-primary/10 via-background to-background py-16">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0iY3VycmVudENvbG9yIiBzdHJva2Utd2lkdGg9IjEiIG9wYWNpdHk9IjAuMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-40" />
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">
              Astrološki Forum
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              Postavite pitanje i dobijte odgovor od profesionalnog astrologa. Javna pitanja donose bonus kredit!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to={ROUTE_PATHS.FORUM_ASK}>
                <Button size="lg" className="bg-gradient-to-r from-primary to-accent hover:opacity-90 transition-opacity">
                  <MessageCircle className="mr-2 h-5 w-5" />
                  Postavi Pitanje
                </Button>
              </Link>
              <Link to={ROUTE_PATHS.FORUM_MY_QUESTIONS}>
                <Button size="lg" variant="outline">
                  <User className="mr-2 h-5 w-5" />
                  Moja Pitanja
                </Button>
              </Link>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="max-w-4xl mx-auto"
          >
            <Card className="mb-8 border-primary/20 shadow-lg">
              <CardContent className="p-6">
                <div className="grid gap-4 md:grid-cols-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Pretraži pitanja..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  
                  <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="Kategorija" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sve">Sve kategorije</SelectItem>
                      <SelectItem value="ljubav">{CATEGORY_LABELS.ljubav}</SelectItem>
                      <SelectItem value="karijera">{CATEGORY_LABELS.karijera}</SelectItem>
                      <SelectItem value="zdravlje">{CATEGORY_LABELS.zdravlje}</SelectItem>
                      <SelectItem value="finansije">{CATEGORY_LABELS.finansije}</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sve">Svi statusi</SelectItem>
                      <SelectItem value="answered">Odgovoreno</SelectItem>
                      <SelectItem value="pending">Na čekanju</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            <div className="space-y-6">
              <AnimatePresence mode="popLayout">
                {filteredQuestions.map((question, index) => (
                  <motion.div
                    key={question.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                  >
                    <Card className="border-border/50 hover:border-primary/30 transition-all duration-300 hover:shadow-lg">
                      <CardHeader>
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Badge variant="secondary" className="text-xs">
                                {CATEGORY_LABELS[question.category]}
                              </Badge>
                              {question.status === 'answered' ? (
                                <Badge className="bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20">
                                  Odgovoreno
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="border-yellow-500/20 text-yellow-600 dark:text-yellow-400">
                                  Na čekanju
                                </Badge>
                              )}
                            </div>
                            <CardTitle className="text-lg mb-2">{question.question}</CardTitle>
                            
                            {/* Birth Data */}
                            {(question.birth_date || question.birth_time || question.birth_place) && (
                              <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground bg-muted/30 rounded-lg p-2 mb-2">
                                {question.birth_date && (
                                  <div className="flex items-center gap-1">
                                    <Calendar className="h-3 w-3" />
                                    <span>{new Date(question.birth_date).toLocaleDateString('sr-RS')}</span>
                                  </div>
                                )}
                                {question.birth_time && (
                                  <div className="flex items-center gap-1">
                                    <Clock className="h-3 w-3" />
                                    <span>{question.birth_time}</span>
                                  </div>
                                )}
                                {question.birth_place && (
                                  <div className="flex items-center gap-1">
                                    <MapPin className="h-3 w-3" />
                                    <span>{question.birth_place}</span>
                                  </div>
                                )}
                              </div>
                            )}
                            
                            <div className="flex items-center gap-4 text-sm text-muted-foreground">
                              <div className="flex items-center gap-1">
                                <User className="h-3 w-3" />
                                <span>{question.display_name}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                <span>{formatDate(question.created_at)}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </CardHeader>
                      
                      {question.answer && (
                        <CardContent>
                          <div className="bg-muted/30 rounded-lg p-4 border-l-4 border-accent">
                            <div className="flex items-center gap-2 mb-2">
                              <div className="h-8 w-8 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-white text-sm font-semibold">
                                A
                              </div>
                              <div>
                                <p className="text-sm font-medium">Odgovor Astrologa</p>
                                <p className="text-xs text-muted-foreground">
                                  {question.answered_at && formatDate(question.answered_at)}
                                </p>
                              </div>
                            </div>
                            <p className="text-sm leading-relaxed mt-3">{question.answer}</p>
                          </div>
                          
                          <div className="flex items-center gap-4 mt-4">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleLike(question.id)}
                              className="gap-2 hover:text-accent"
                            >
                              <Heart className="h-4 w-4" />
                              <span>{question.likes}</span>
                            </Button>
                          </div>
                        </CardContent>
                      )}
                    </Card>
                  </motion.div>
                ))}
              </AnimatePresence>

              {filteredQuestions.length === 0 && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-center py-12"
                >
                  <Filter className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                  <p className="text-lg text-muted-foreground">Nema pitanja koja odgovaraju filterima</p>
                  <p className="text-sm text-muted-foreground mt-2">Pokušajte sa drugačijim kriterijumima pretrage</p>
                </motion.div>
              )}
            </div>
          </motion.div>
        </div>
      </div>

      <div className="bg-muted/30 py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="border-primary/20">
              <CardContent className="p-8 text-center">
                <h3 className="text-2xl font-semibold mb-4">Nemate dovoljno kredita?</h3>
                <p className="text-muted-foreground mb-6">
                  Kupite paket kredita i postavite svoja pitanja. Javna pitanja donose bonus kredit!
                </p>
                <Link to={ROUTE_PATHS.FORUM_CREDITS}>
                  <Button size="lg" className="bg-gradient-to-r from-primary to-accent hover:opacity-90">
                    Kupi Kredite
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}